/** Что выделено в поле: текст целиком и границы выделения */
export type TextSelection = { value: string; start: number; end: number };

/** Замена куска текста `[from, to)` на `insert` и новое выделение */
export type TextEdit = { from: number; to: number; insert: string; select: [number, number] };

/** `**жирный**`, `_курсив_`: оборачивает выделение, повторное нажатие снимает разметку */
export const wrapSelection = ({
  selection: { value, start, end },
  marker,
}: {
  selection: TextSelection;
  marker: string;
}): TextEdit => {
  const length = marker.length;
  const selected = value.slice(start, end);
  const isWrapped =
    value.slice(start - length, start) === marker && value.slice(end, end + length) === marker;

  if (isWrapped) {
    return {
      from: start - length,
      to: end + length,
      insert: selected,
      select: [start - length, end - length],
    };
  }

  return {
    from: start,
    to: end,
    insert: `${marker}${selected}${marker}`,
    select: [start + length, end + length],
  };
};

export type LineFormat = 'heading' | 'bullet' | 'numbered' | 'check' | 'quote';

const LINE_PREFIX: Record<LineFormat, { pattern: RegExp; prefix: (index: number) => string }> = {
  heading: { pattern: /^#{1,6} /, prefix: () => '## ' },
  bullet: { pattern: /^[-*+] (?!\[[ xX]\] )/, prefix: () => '- ' },
  numbered: { pattern: /^\d+[.)] /, prefix: (index) => `${index + 1}. ` },
  check: { pattern: /^[-*+] \[[ xX]\] /, prefix: () => '- [ ] ' },
  quote: { pattern: /^> ?/, prefix: () => '> ' },
};

/** Строки, которых касается выделение, целиком */
const selectedLines = ({ value, start, end }: TextSelection) => {
  // lastIndexOf с отрицательной позицией всё равно смотрит символ 0 — первую строку берём явно
  const from = start === 0 ? 0 : value.lastIndexOf('\n', start - 1) + 1;
  const lineEnd = value.indexOf('\n', end);
  const to = lineEnd === -1 ? value.length : lineEnd;

  return { from, to, lines: value.slice(from, to).split('\n') };
};

/** Списки, заголовок, цитата: если формат уже у всех строк — снимает, иначе ставит (чужой меняет) */
export const toggleLines = ({
  selection,
  format,
}: {
  selection: TextSelection;
  format: LineFormat;
}): TextEdit => {
  const { from, to, lines } = selectedLines(selection);
  const { pattern, prefix } = LINE_PREFIX[format];
  const isApplied = lines.every((line) => pattern.test(line));
  const anyPattern = Object.values(LINE_PREFIX).map((p) => p.pattern);
  const strip = (line: string) => anyPattern.reduce((rest, other) => rest.replace(other, ''), line);
  const insert = lines
    .map((line, index) => (isApplied ? line.replace(pattern, '') : prefix(index) + strip(line)))
    .join('\n');

  // Пустая строка: курсор после префикса, чтобы сразу писать
  if (from === to)
    return { from, to, insert, select: [from + insert.length, from + insert.length] };

  return { from, to, insert, select: [from, from + insert.length] };
};

/** `[текст](https://)`: курсор — на место адреса */
export const insertLink = ({ value, start, end }: TextSelection): TextEdit => {
  const text = value.slice(start, end) || 'ссылка';
  const insert = `[${text}](https://)`;
  const cursor = start + insert.length - 1;

  return { from: start, to: end, insert, select: [cursor, cursor] };
};

/** Блок кода вокруг выделенных строк */
export const insertCodeBlock = (selection: TextSelection): TextEdit => {
  const { from, to, lines } = selectedLines(selection);
  const body = lines.join('\n');
  const insert = `\`\`\`\n${body}\n\`\`\``;

  return { from, to, insert, select: [from + 4, from + 4 + body.length] };
};
